export interface ReaderTheme {
  readonly backgroundColor: string;
  readonly id: string;
  readonly label: string;
  readonly linkColor: string;
  readonly textColor: string;
}

const THEME_LINK_ELEMENT_ID = 'theme-link';

/**
 * Kept in sync with the external theme stylesheets served from
 * https://themes.odilo.io/NubeReader_<id>/_theme.css, which restyle the whole app shell
 * (header, footer, buttons, …) via CSS custom properties, not just the epub content.
 */
export const READER_THEMES: readonly ReaderTheme[] = [
  {
    backgroundColor: 'rgb(255, 255, 255)',
    textColor: 'rgba(0, 0, 0, 0.9)',
    linkColor: 'rgba(0, 0, 0, 0.9)',
    id: 'White',
    label: 'SETTINGS.THEMES.WHITE',
  },
  {
    backgroundColor: 'rgb(245, 238, 224)',
    textColor: 'rgba(0, 0, 0, 0.9)',
    linkColor: 'rgba(0, 0, 0, 0.9)',
    id: 'Sepia',
    label: 'SETTINGS.THEMES.SEPIA',
  },
  {
    backgroundColor: 'rgb(59, 59, 59)',
    textColor: 'rgba(255, 255, 255, 0.9)',
    linkColor: 'rgba(255, 255, 255, 0.9)',
    id: 'DarkGrey',
    label: 'SETTINGS.THEMES.DARKGREY',
  },
  {
    backgroundColor: 'rgb(0, 0, 0)',
    textColor: 'rgba(255, 255, 255, 0.9)',
    linkColor: 'rgba(255, 255, 255, 0.9)',
    id: 'Black',
    label: 'SETTINGS.THEMES.BLACK',
  },
];

const HEX_COLOR_PATTERN = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;
const HEX_RADIX = 16;

/**
 * Normalizes a persisted color to the `rgb(r, g, b)` format used by READER_THEMES.
 * Older clients stored themes as 6-digit hex (e.g. `#3B3B3B`) before a migration to
 * `rgb(...)` strings — without this, a legacy hex value never matches a known theme.
 */
function normalizeColorToRgb(color: string | null | undefined): string | undefined {
  if (!color) return undefined;
  const trimmed = color.trim();
  const hexMatch = HEX_COLOR_PATTERN.exec(trimmed);
  if (!hexMatch) return trimmed;
  const [, redHex, greenHex, blueHex] = hexMatch;
  const r = Number.parseInt(redHex, HEX_RADIX);
  const g = Number.parseInt(greenHex, HEX_RADIX);
  const b = Number.parseInt(blueHex, HEX_RADIX);
  return `rgb(${r}, ${g}, ${b})`;
}

interface RgbColor {
  readonly b: number;
  readonly g: number;
  readonly r: number;
}

const RGB_COLOR_PATTERN = /^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/i;

function parseRgb(color: string): RgbColor | undefined {
  const match = RGB_COLOR_PATTERN.exec(color);
  if (!match) return undefined;
  const [, r, g, b] = match;
  return { r: Number(r), g: Number(g), b: Number(b) };
}

function square(value: number): number {
  return value * value;
}

function colorDistance(a: RgbColor, b: RgbColor): number {
  return Math.sqrt(square(a.r - b.r) + square(a.g - b.g) + square(a.b - b.b));
}

const MAX_MATCHING_COLOR_DISTANCE = 30;

export function findReaderThemeByBackgroundColor(
  backgroundColor: string | null | undefined,
): ReaderTheme | undefined {
  const normalized = normalizeColorToRgb(backgroundColor);
  if (!normalized) return undefined;

  const exactMatch = READER_THEMES.find((theme) => theme.backgroundColor === normalized);
  if (exactMatch) return exactMatch;

  const targetColor = parseRgb(normalized);
  if (!targetColor) return undefined;

  let closestTheme: ReaderTheme | undefined = undefined;
  let closestDistance = Number.POSITIVE_INFINITY;
  for (const theme of READER_THEMES) {
    const themeColor = parseRgb(theme.backgroundColor);
    if (!themeColor) continue;
    const distance = colorDistance(targetColor, themeColor);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestTheme = theme;
    }
  }
  return closestDistance <= MAX_MATCHING_COLOR_DISTANCE ? closestTheme : undefined;
}

export function findReaderThemeById(id: string): ReaderTheme | undefined {
  return READER_THEMES.find((theme) => theme.id === id);
}

const THEME_STORAGE_KEY = 'NUBEREADER_THEME';

export function getPersistedThemeId(): string | undefined {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    return undefined;
  }
  return stored && findReaderThemeById(stored) ? stored : undefined;
}

/** Returns false when storage is unavailable, so callers can ignore the failure. */
export function persistThemeId(themeId: string): boolean {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, themeId);
    return true;
  } catch {
    return false;
  }
}

let pendingThemeLoads: (() => void)[] = [];

function settlePendingThemeLoads(): void {
  const settleCallbacks = pendingThemeLoads;
  pendingThemeLoads = [];
  settleCallbacks.forEach((settle) => {
    settle();
  });
}

export async function applyDocumentTheme(themeId: string): Promise<void> {
  const link = document.querySelector<HTMLLinkElement>(`#${THEME_LINK_ELEMENT_ID}`);
  if (!link) return;
  const newHref = `https://themes.odilo.io/NubeReader_${themeId}/_theme.css`;
  if (link.href === newHref) return;

  settlePendingThemeLoads();

  return new Promise<void>((resolve) => {
    const settle = (): void => {
      link.removeEventListener('load', settle);
      link.removeEventListener('error', settle);
      pendingThemeLoads = pendingThemeLoads.filter((callback) => callback !== settle);
      resolve();
    };
    pendingThemeLoads.push(settle);
    link.addEventListener('load', settle, { once: true });
    link.addEventListener('error', settle, { once: true });
    link.href = newHref;
  });
}

export function initializeDocumentTheme(): void {
  const themeId = getPersistedThemeId();
  if (!themeId) return;
  void applyDocumentTheme(themeId);
}
